import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, makeStyles, Typography, Button, TextField, MenuItem } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import PricingConditionList from './pricingConditionList/PricingConditionList'
import PricingConditionCard from './pricingConditionCard/PricingConditionCard'
import { useCalcHeightToBottom } from '../../utils/ui'
import PricingConditionAddEditDialog from './pricingConditionAddEditDialog/PricingConditionAddEditDialog'
import { DeleteConfirmationDialog } from '../../shared/components/deleteConfirmationDialog'
import { PricingConditionModel } from '../../shared/types/pricingCondition'
import { deletePricingCondition, fetchPricingConditions } from '../../redux/actions/pricingConditions'
import { fetchTariffs } from '../../redux/actions/tariffs'
import { fetchTags } from '../../redux/actions/tags'
import { Tenant, fetchTenants } from '../../redux/actions/tenants'
import { TagModel, TagTypes } from '../../shared/types/tag'
import { TariffModel } from '../../shared/types/tariff'
import { config } from '../../config'

const useStyles = makeStyles((theme) => ({
    headerWrapper: {
        padding: theme.spacing(1),
    },
    actionButtonWrapper: {
        padding: theme.spacing(1, 0),
        textAlign: 'right',
    },
    listWrapper: {
        paddingTop: theme.spacing(2),
    },
    cardWrapper: {
        paddingTop: theme.spacing(2),
        width: '100%',
    },
    filterWrapper: {
        padding: theme.spacing(1),
        paddingRight: 0,
        display: 'flex',
        gap: theme.spacing(2),
    },
    input: {
        backgroundColor: '#f5f5f5', 
        borderRadius: theme.shape.borderRadius, 
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5', 
            borderRadius: theme.shape.borderRadius, 
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none', 
            },
        },
        '& .MuiInputLabel-root': {
            top: '-7px', 
            fontSize: '0.9rem', 
            // color: '#9e9e9e',
        },
        '& .MuiSelect-icon': {
            top: 'calc(50% - 12px)',
        },
    }
}))

export default function PricingConditions() {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const [selectedPricingCondition, setSelectedPricingCondition] = useState<PricingConditionModel>()

    const [addEditDialogState, setAddEditDialogState] = useState({
        open: false,
        isAdd: true,
        onClose: (pricingCondition: PricingConditionModel) => {},
        initialModel: undefined as PricingConditionModel | undefined,
    })

    const [addedPricingCondition, setAddedPricingCondition] = useState<PricingConditionModel>()
    const [editedPricingCondition, setEditedPricingCondition] = useState<PricingConditionModel>()
    const [pricingConditionToDelete, setPricingConditionToDelete] = useState<PricingConditionModel>()

    const [tenantIdFilter, setTenantIdFilter] = useState('')
    const [chargerTagFilter, setChargerTagFilter] = useState('')
    const [locationTagFilter, setLocationTagFilter] = useState('')
    const [userTagFilter, setUserTagFilter] = useState('')
    const [chargingProfileTagFilter, setChargingProfileTagFilter] = useState('')
    const [scheduleFilter, setScheduleFilter] = useState('')

    const tags = useSelector((state: any) => state.tags.items.all.value)
    const tariffs = useSelector((state: any) => state.tariffs.items.value)
    const tenants = useSelector((state: any) => state.tenants.value)

    const userTags = (tags || []).filter((tag: TagModel) => tag.type === TagTypes[0].code)
    const chargerTags = (tags || []).filter((tag: TagModel) => tag.type === TagTypes[1].code)
    const locationTags = (tags || []).filter((tag: TagModel) => tag.type === TagTypes[2].code)
    const chargingProfileTags = (tags || []).filter((tag: TagModel) => tag.type === TagTypes[3].code)

    const pricingConditions = useSelector((state: any) => state.pricingConditions.items.value)
    const deletedPricingCondition = useSelector((state: any) => state.pricingConditions.delete.value)
    const pricingConditionDeleteExecuted = useSelector((state: any) => state.pricingConditions.delete.executed)

    const pricingConditionCardHeightFill = useCalcHeightToBottom('pricingConditionsCardWrapper')

    const openAddDialog = () => {
        setAddEditDialogState({
            open: true,
            isAdd: true,
            onClose: onAddDialogClose,
            initialModel: undefined,
        })
    }

    const onAddDialogClose = (pricingCondition?: PricingConditionModel) => {
        setAddEditDialogState({ ...addEditDialogState, open: false })
        if (pricingCondition) {
            setAddedPricingCondition(pricingCondition)
        }
    }

    const onEditDialogClose = (pricingCondition?: PricingConditionModel) => {
        setAddEditDialogState({ ...addEditDialogState, open: false })
        if (pricingCondition) {
            setEditedPricingCondition(pricingCondition)
        }
    }

    const onPricingConditionSelected = (pricingCondition: PricingConditionModel) => {
        setSelectedPricingCondition(pricingCondition)
    }

    const onPricingConditionDelete = (pricingCondition: PricingConditionModel) => {
        setPricingConditionToDelete(pricingCondition)
    }

    const onPricingConditionEdit = (pricingCondition: PricingConditionModel) => {
        setAddEditDialogState({
            open: true,
            isAdd: false,
            onClose: onEditDialogClose,
            initialModel: pricingCondition,
        })
    }
    const handleRemovePricingCondition = async () => {
        const pricingConditionToDeleteDefined = pricingConditionToDelete as PricingConditionModel
        await dispatch(deletePricingCondition(pricingConditionToDeleteDefined))
        setPricingConditionToDelete(undefined)
    }

    useEffect(() => {
        dispatch(fetchPricingConditions())
    }, [dispatch, addedPricingCondition, editedPricingCondition, deletedPricingCondition])

    useEffect(() => {
        dispatch(fetchTariffs(tenantId))
        dispatch(fetchTags())
        dispatch(fetchTenants())
    }, [dispatch, tenantId])

    const filteredPricingConditions = pricingConditions.filter((condition: PricingConditionModel) => {
        return (
            (!tenantIdFilter || condition.tenantId === tenantIdFilter) &&
            (!chargerTagFilter || condition.chargerTag === chargerTagFilter) &&
            (!locationTagFilter || condition.locationTag === locationTagFilter) &&
            (!userTagFilter || condition.userTag === userTagFilter) &&
            (!chargingProfileTagFilter || condition.chargingProfileTag === chargingProfileTagFilter) &&
            (!scheduleFilter || condition.scheduleTariff === scheduleFilter)
        )
    })

    useEffect(() => {
        if (pricingConditions.length > 0) {
            if (selectedPricingCondition != null) {
                const pricingConditionToSelect = pricingConditions.find(
                    (x: any) => x.id === selectedPricingCondition.id
                )
                if (pricingConditionToSelect != null) {
                    setSelectedPricingCondition(pricingConditionToSelect)
                } else {
                    setSelectedPricingCondition(pricingConditions[0])
                }
            } else {
                setSelectedPricingCondition(pricingConditions[0])
            }
        } else {
            setSelectedPricingCondition(undefined)
        }
    }, [pricingConditions, selectedPricingCondition])

    return (
        <>
            <Grid container spacing={0}>
                <Grid item sm={2} className={classes.headerWrapper}>
                    <Typography align="left" variant="h3">
                        {t('pricingConditions.title')}
                    </Typography>
                </Grid>
                <Grid item sm={10} className={classes.actionButtonWrapper}>
                    <Button
                        onClick={() => {
                            openAddDialog()
                        }}
                        variant="contained"
                        color="primary"
                    >
                        {t('pricingConditions.newPricingConditions')}
                    </Button>
                    {addEditDialogState.open && <PricingConditionAddEditDialog {...addEditDialogState} />}
                </Grid>
                <Grid item xs={12} className={classes.filterWrapper}>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('tenant')}
                            disabled={tenantId != config.mainTenantId}
                            value={tenantIdFilter}
                            onChange={(e) => setTenantIdFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(tenants || []).map((tenant: Tenant) => (
                                <MenuItem key={tenant.id} value={tenant.id}>
                                    {tenant.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('chargerTag')}
                            value={chargerTagFilter}
                            onChange={(e) => setChargerTagFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(chargerTags || []).map((tag: TagModel) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    {tag.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('locationTag')}
                            value={locationTagFilter}
                            onChange={(e) => setLocationTagFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(locationTags || []).map((tag: TagModel) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    {tag.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('userTag')}
                            value={userTagFilter}
                            onChange={(e) => setUserTagFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(userTags || []).map((tag: TagModel) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    {tag.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('chargingProfileTag')}
                            value={chargingProfileTagFilter}
                            onChange={(e) => setChargingProfileTagFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(chargingProfileTags || []).map((tag: TagModel) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    {tag.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('schedule')}
                            value={scheduleFilter}
                            onChange={(e) => setScheduleFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(tariffs || []).map((tariff: TariffModel) => (
                                <MenuItem key={tariff.id} value={tariff.id}>
                                    {tariff.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Grid item sm={4} className={classes.listWrapper}>
                    <PricingConditionList
                        selectedPricingCondition={selectedPricingCondition}
                        pricingConditions={filteredPricingConditions}
                        onPricingConditionSelected={onPricingConditionSelected}
                    />
                </Grid>

                <Grid item sm={8} className={classes.cardWrapper}>
                    <div id="pricingConditionCardWrapper" ref={pricingConditionCardHeightFill}>
                        {selectedPricingCondition && (
                            <PricingConditionCard
                                pricingCondition={selectedPricingCondition}
                                onDelete={() => onPricingConditionDelete(selectedPricingCondition)}
                                onEdit={() => onPricingConditionEdit(selectedPricingCondition)}
                            />
                        )}
                    </div>
                </Grid>
            </Grid>
            <DeleteConfirmationDialog
                open={Boolean(pricingConditionToDelete)}
                onConfirm={async () => {
                    handleRemovePricingCondition()
                }}
                content={t('pricingConditions.deleteContent')}
                onCancel={() => setPricingConditionToDelete(undefined)}
                inProgress={!pricingConditionDeleteExecuted}
            />
        </>
    )
}
